import { z } from "zod";

export const SaveSummaryLifecycleStateSchema = z.enum([
  "DRAFT_AUDIENCE",
  "DRAFT_SEQUENCE",
  "DRAFT_REVIEW",
  "ACTIVE",
  "PAUSED",
  "DEACTIVATED",
  "ARCHIVED",
]);

export const SaveSummaryTierSchema = z.enum([
  "ESSENTIAL",
  "CLINICAL",
  "GROWTH",
  "CUSTOM",
]);
// The backend's `_effective_trigger_mode` can return "" for a custom draft whose
// trigger_mode has not been set yet (recall/services/draft_review_service.py).
// Hedge with z.string() so the page payload still validates on the happy path
// rather than silently falling back to the unparsed raw object.
export const SaveSummaryTriggerModeSchema = z
  .enum(["SCHEDULED", "EVENT", "HYBRID"])
  .or(z.string());
export const SaveSummaryChannelLowerSchema = z
  .enum(["email", "sms", "voice", "human"])
  .or(z.string());
export const SaveSummaryChannelUpperSchema = z
  .enum(["EMAIL", "SMS", "VOICE", "HUMAN_CALL"])
  .or(z.string());

export const SaveSummarySequenceStatusSchema = z
  .enum(["DRAFT", "ACTIVE", "PAUSED", "ARCHIVED"])
  .or(z.string());
export const SaveSummarySegmentLifecycleSchema = z
  .enum(["DRAFT", "ACTIVE", "ARCHIVED"])
  .or(z.string());

export const SaveSummaryHeaderSchema = z.object({
  title: z.string(),
  subtitle: z.string(),
  lifecycle_state: SaveSummaryLifecycleStateSchema,
  draft_origin: z.enum(["library", "custom"]),
  is_custom: z.boolean(),
});

export const SaveSummaryRecallSchema = z.object({
  id: z.number(),
  name: z.string(),
  display_name: z.string(),
  custom_name: z.string().nullable(),
  description: z.string(),
  why_this_matters: z.string(),
  goal: z.object({ slug: z.string(), label: z.string() }),
  template: z
    .object({
      id: z.number(),
      slug: z.string(),
      name: z.string(),
    })
    .nullable(),
  tier: SaveSummaryTierSchema,
  trigger_mode: SaveSummaryTriggerModeSchema,
  trigger_rule_description: z.string(),
  supported_channels: z.array(SaveSummaryChannelLowerSchema),
  daily_enrollment_cap: z.number(),
  lifecycle_state: SaveSummaryLifecycleStateSchema,
  draft_origin: z.enum(["library", "custom"]),
  // Backend `_serialize_recall` emits null for these when the draft timestamps
  // are unset (recall/services/draft_review_service.py).
  draft_started_at: z.string().nullable(),
  draft_last_activity_at: z.string().nullable(),
});

export const SaveSummarySummarySchema = z.object({
  segment_count: z.number(),
  active_segment_count: z.number(),
  sequence_count: z.number(),
});

export const SaveSummaryEmbeddedSequenceSchema = z.object({
  // NOTE: the backend `_build_inspection` (recall/services/draft_review_service.py)
  // sets sequence.id to null when a segment has no sequence yet. We keep this as a
  // non-null number here because the SequenceBuilderView segment contract requires
  // `sequenceId: number`. A genuinely sequence-less segment is a flagged error
  // state on the backend; if one is returned, the strict parse fails and
  // useSaveSummary falls back to the reconciled raw payload (consumers read the
  // fields they need defensively). Coercing here instead would mask the error.
  id: z.number(),
  name: z.string(),
  status: SaveSummarySequenceStatusSchema,
  node_count: z.number(),
  action_count: z.number(),
  channels: z.array(SaveSummaryChannelLowerSchema),
  validation_errors: z.array(z.string()),
});

export const SaveSummarySegmentSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  lifecycle_state: SaveSummarySegmentLifecycleSchema,
  interval_display: z.string(),
  // Backend `_preview_segment_patient_count` returns null on a preview error,
  // and patient counts are omitted entirely on the activate path
  // (recall/services/draft_review_service.py, include_patient_counts=False).
  patient_count: z.number().nullable(),
  patient_warnings: z.array(z.string()),
  sequence: SaveSummaryEmbeddedSequenceSchema,
});

export const SaveSummaryKpiConfigSchema = z.object({
  id: z.number(),
  location_recall: z.number(),
  kpi_slug: z.string(),
  display_order: z.number(),
  is_active: z.boolean(),
});

export const SaveSummaryActivationChecksSchema = z.object({
  ready: z.boolean(),
  errors: z.array(z.string()),
  warnings: z.array(z.string()),
});

export const SaveSummaryPageSchema = z.object({
  header: SaveSummaryHeaderSchema,
  recall: SaveSummaryRecallSchema,
  summary: SaveSummarySummarySchema,
  segments: z.array(SaveSummarySegmentSchema),
  kpis: z.array(SaveSummaryKpiConfigSchema),
  activation_checks: SaveSummaryActivationChecksSchema,
});

export const SaveSummaryActivateSuccessSchema = z.object({
  success: z.literal(true),
  location_recall_id: z.number(),
  lifecycle_state: SaveSummaryLifecycleStateSchema,
  segments_activated: z.number(),
  sequences_activated: z.number(),
  activation_warnings: z.array(z.string()),
  kpi_defaults: z.record(z.string(), z.unknown()).optional(),
  starter_template_manifest: z.record(z.string(), z.unknown()).optional(),
  next_step: z.string().optional(),
});

export const SaveSummaryActivateFailureSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  message: z.string().optional(),
  activation_errors: z.array(z.string()).default([]),
  activation_warnings: z.array(z.string()).default([]),
  field_errors: z.record(z.string(), z.array(z.string())).optional(),
});

export const SaveSummaryActivateResponseSchema = z.discriminatedUnion("success", [
  SaveSummaryActivateSuccessSchema,
  SaveSummaryActivateFailureSchema,
]);

export const KpiRegistryItemSchema = z.object({
  slug: z.string(),
  label: z.string().optional(),
  name: z.string().optional(),
});

export const KpiRegistrySchema = z.array(KpiRegistryItemSchema);

export type SaveSummaryPage = z.infer<typeof SaveSummaryPageSchema>;
export type SaveSummaryRecall = z.infer<typeof SaveSummaryRecallSchema>;
export type SaveSummarySegment = z.infer<typeof SaveSummarySegmentSchema>;
export type SaveSummaryActivationChecks = z.infer<
  typeof SaveSummaryActivationChecksSchema
>;
export type SaveSummaryActivateResponse = z.infer<
  typeof SaveSummaryActivateResponseSchema
>;
export type SaveSummaryLifecycleState = z.infer<
  typeof SaveSummaryLifecycleStateSchema
>;
export type SaveSummaryTriggerMode = z.infer<typeof SaveSummaryTriggerModeSchema>;

export type SaveSummaryActivateBody = {
  display_name?: string;
  name?: string;
  description?: string;
  goal_type_slug?: string;
  trigger_mode?: SaveSummaryTriggerMode;
  trigger_rule_description?: string;
  why_this_matters?: string;
  supported_channels?: string[];
  daily_enrollment_cap?: number;
};

export type KpiRegistryItem = z.infer<typeof KpiRegistryItemSchema>;
